import { useState } from "react";
import { useWorkflows, useEnrollWorkflow } from "@/hooks/useWorkflows";

export function WorkflowEnrollPicker({
  leadIds,
  accountIds,
  apolloPersonIds,
  onClose,
}: {
  leadIds?: string[];
  accountIds?: string[];
  apolloPersonIds?: string[];
  onClose: () => void;
}) {
  const listQuery = useWorkflows({ status: "active" });
  const enrollMut = useEnrollWorkflow();
  const [message, setMessage] = useState<string | null>(null);
  const workflows = listQuery.data?.workflows ?? [];
  const count =
    leadIds?.length ||
    apolloPersonIds?.length ||
    accountIds?.length ||
    0;

  async function enroll(workflowId: string) {
    setMessage(null);
    try {
      const res = await enrollMut.mutateAsync({
        workflowId,
        leadIds,
        accountIds,
        apolloPersonIds,
      });
      setMessage(
        `Enrolled ${res.enrollments.length}` +
          (res.skipped.length
            ? `, skipped ${res.skipped.length} (save contacts first if needed)`
            : ""),
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Enroll failed");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-background p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add to workflow</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          {count} record(s) — enroll directly (bypass trigger). Saved CRM
          contacts enroll immediately; unsaved Apollo people are skipped.
        </p>
        {listQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-7 w-7 animate-spin rounded-full border-4 border-muted border-t-brand" />
          </div>
        ) : workflows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No active workflows. Activate one under Workflows first.
          </p>
        ) : (
          <ul className="max-h-72 space-y-2 overflow-y-auto">
            {workflows.map((w) => (
              <li key={w.workflowId}>
                <button
                  type="button"
                  disabled={enrollMut.isPending}
                  onClick={() => void enroll(w.workflowId)}
                  className="w-full rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-muted/40 disabled:opacity-60"
                >
                  <span className="font-medium">{w.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {w.trigger.type}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
}
